import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import usePermission from '../../hooks/usePermission';
import { ParsedCSVData } from '../../interfaces/index.types';
import { doc, onSnapshot } from '../../libs/firestore-wrapper';
import { getCurrentSettings, getStadiumConfig } from '../../modals/order';
import { ISettings, IStadium } from '../../pages/Settings/settings.types';
import { fetchCSVFromStorage } from '../../utils';

interface SettingsContextValue {
  currentSetting: ISettings;
  stadiumData: IStadium;
  loading: boolean;
  setCurrentSetting: React.Dispatch<React.SetStateAction<ISettings>>;
  seatData: ParsedCSVData;
}

const init_settings = {
  advertisement_tags: [],
  client_lang_display: '',
  ip_printer: '',
  enable_rest_ad_control: false,
  isEnableBellRing: false,
  isSkippableToAssignTable: false,
  lineup: {
    lineup_table_placeholder: '',
    lineup_username_placeholder: '',
    max_num_of_adult: 0,
    max_num_of_children: 0,
  },
  meny_service_class: {
    id: 0,
    name: '',
  },
  isOnline: true,
  name: '',
  opening_schedule: {
    SUN: { enabled: false, utc_start_time: '', utc_end_time: '' },
    MON: { enabled: false, utc_start_time: '', utc_end_time: '' },
    TUE: { enabled: false, utc_start_time: '', utc_end_time: '' },
    WED: { enabled: false, utc_start_time: '', utc_end_time: '' },
    THU: { enabled: false, utc_start_time: '', utc_end_time: '' },
    FRI: { enabled: false, utc_start_time: '', utc_end_time: '' },
    SAT: { enabled: false, utc_start_time: '', utc_end_time: '' },
  },
  region: '',
  restaurant_open_id: '',
  restaurant_open_state: 0,
  restaurant_operators: [],
  restaurant_profile_pic: '',
  service_tags: [],
  stadium_id: '',
  status: '',
  merchant_id: '',
  location_id: '',
  large_paper: false,
};

const initStadium = {
  administrators: [],
  super_administrators: [],
  enable_sales_count: false,
  seat_number: '',
  fileUrl: '',
  id: '',
  isSeeAll: false,
  isStadiumPromotion: false,
  last_update_date: '',
  latest_id: 0,
  name: '',
  promotion_restaurant_id: '',
  service_name: '',
  stadium_profile_pic: '',
  status: '',
  elepay_secret_key: '',
  elepay_public_key: '',
  location_id: '',
  merchant_id: '',
  riders: [],
  enable_drink_quantity_control: false,
  enable_food_quantity_control: false,
  max_drink_quantity: 6,
  max_food_quantity: 10,
  phone: '',
  address: '',
  registration_code: '',
  business_name: '',
};

const SettingsContext = createContext<SettingsContextValue>({
  currentSetting: init_settings,
  stadiumData: initStadium,
  loading: false,
  setCurrentSetting: () => {},
  seatData: [],
});

const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(true);
  let [currentSetting, setCurrentSetting] = useState<ISettings>(init_settings);
  const [stadiumData, setStadiumData] = useState<IStadium>(initStadium);
  let [seatData, setSeatData] = useState<ParsedCSVData>([]);
  const { restaurantId } = usePermission();

  const fetchData = useCallback(async () => {
    if (!restaurantId) return;

    setLoading(true);

    const settingsRef = getCurrentSettings(restaurantId);
    const unsubscribeSettings = onSnapshot(
      settingsRef,
      async (snapshot: any) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as ISettings;
          setCurrentSetting(data);
          let unsubscribeStadium: (() => void) | null = null;

          if (data?.stadium_id) {
            const seatInformation = await fetchCSVFromStorage(data.stadium_id);
            setSeatData(seatInformation);
            const stadiumRef = doc(getStadiumConfig(), data.stadium_id);
            unsubscribeStadium = onSnapshot(
              stadiumRef,
              (restaurantConfig: any) => {
                if (restaurantConfig.exists()) {
                  setStadiumData({ ...(restaurantConfig.data() as IStadium), id: restaurantConfig.id });
                }
              },
              (error) => {
                console.error('Error fetching stadium data:', error);
              }
            );
          }

          setLoading(false);

          return () => {
            unsubscribeSettings();
            if (unsubscribeStadium) unsubscribeStadium();
          };
        } else {
          console.warn('No data found for the provided restaurant ID.');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error fetching settings data:', error);
        setLoading(false);
      }
    );

    return unsubscribeSettings;
  }, [restaurantId]);

  useEffect(() => {
    if (restaurantId) {
      fetchData();
    }
  }, [restaurantId, fetchData]);

  return (
    <SettingsContext.Provider
      value={{
        loading,
        currentSetting,
        stadiumData,
        setCurrentSetting,
        seatData,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  return context;
};

export { SettingsProvider, useSettingsContext };

